import React, { useEffect, useState, useCallback } from "react";
import { Row, Col, Card, Button, Modal } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChartBar, faChevronDown } from "@fortawesome/free-solid-svg-icons";
import axios from "axios";
import { ApiConfig } from "config/ApiConfig";
import { getApiConfig } from "helpers/utils";
import { FilterProvider } from "context/Filter";
import "primereact/resources/themes/saga-blue/theme.css";
import "primereact/resources/primereact.min.css";
// Tremor CSS is included in the package, no need to import separately
import "./main.css";
import { DateRangePicker } from "rsuite";
import "rsuite/dist/rsuite.min.css";
import subDays from "date-fns/subDays";
import startOfWeek from "date-fns/startOfWeek";
import endOfWeek from "date-fns/endOfWeek";
import addDays from "date-fns/addDays";
import startOfMonth from "date-fns/startOfMonth";
import endOfMonth from "date-fns/endOfMonth";
import addMonths from "date-fns/addMonths";

import ScoreAndSurvey from "./default/ScoreAndSurvey";
import TotalSales from "./default/TotalSales";
import StorageStatus from "./default/StorageStatus";
import XSurveyFeed from "./XSurveyFeed";
import { Dropdown } from "primereact/dropdown";
import Loader from "components/Loader/Loader";


const overGraph = [
  { satisfied: 86, neutral: 0, dissatisfied: 14, total: 14 },
  { satisfied: 75, neutral: 10, dissatisfied: 15, total: 20 },
];

const { afterToday } = DateRangePicker;

const predefinedRanges = [
  {
    label: "Today",
    value: [new Date(), new Date()],
    placement: "left",
  },
  {
    label: "Yesterday",
    value: [addDays(new Date(), -1), addDays(new Date(), -1)],
    placement: "left",
  },
  {
    label: "This week",
    value: [startOfWeek(new Date()), endOfWeek(new Date())],
    placement: "left",
  },
  {
    label: "Last 7 days",
    value: [subDays(new Date(), 6), new Date()],
    placement: "left",
  },
  {
    label: "Last 30 days",
    value: [subDays(new Date(), 29), new Date()],
    placement: "left",
  },
  {
    label: "This month",
    value: [startOfMonth(new Date()), new Date()],
    placement: "left",
  },
  {
    label: "Last month",
    value: [
      startOfMonth(addMonths(new Date(), -1)),
      endOfMonth(addMonths(new Date(), -1)),
    ],
    placement: "left",
  },
  {
    label: "This year",
    value: [new Date(new Date().getFullYear(), 0, 1), new Date()],
    placement: "left",
  },
  {
    label: "Last week",
    closeOverlay: false,
    value: (value) => {
      const [start = new Date()] = value || [];
      return [
        addDays(startOfWeek(start, { weekStartsOn: 0 }), -7),
        addDays(endOfWeek(start, { weekStartsOn: 0 }), -7),
      ];
    },
    appearance: "default",
  },
];

const XSurvey = ({ id }) => {
  const [templateList, setTemplateList] = useState({});
  const [dateRange, setDateRange] = useState([
    startOfMonth(new Date()),
    new Date(),
  ]);
  const [groups, setGroups] = useState("All");
  const [feedbackData, setFeedbackData] = useState([]);
  const [totalFeedback, setTotalFeedback] = useState(0);
  const [receivedFeedback, setReceivedFeedback] = useState(0);
  const [percentage, setPercentage] = useState(0);
  const [progressBarPercentages, setProgressBarPercentages] = useState({});
  const [progressBarCounts, setProgressBarCounts] = useState({});
  // eslint-disable-next-line no-unused-vars
  const [ratingType, setRatingType] = useState("");
  // eslint-disable-next-line no-unused-vars
  const [feedbackType, setFeedbackType] = useState("");
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [groupList, setGroupList] = useState([]);
  const [graphData, setGraphData] = useState([]);
  const [isDataLoaded, setIsDataLoaded] = useState(false);


  const [isLoadingModules, setIsLoadingModules] = useState(false);
  const [score, setScore] = useState(0);


  const [isTemplateDataLoaded, setIsTemplateDataLoaded] = useState(false);
  const [hasNoTemplates, setHasNoTemplates] = useState(false);
  const [showGraph, setShowGraph] = useState(false);
  


  const fetchTemplateData = useCallback(async () => {
    const config = getApiConfig();
    try {
      setIsLoadingModules(true);
      const response = await axios.get(ApiConfig.XSurveyFilter, config);
      const data = response.data;

      if (data && typeof data === "object" && Object.keys(data).length === 0) {
        setHasNoTemplates(true);
        setTemplateList({});
        setIsTemplateDataLoaded(true);
        setIsLoadingModules(false);
        return;
      }

      if (data && Object.keys(data).length > 0) {
        setHasNoTemplates(false);
        setTemplateList(data);
        const defaultTemplate = Object.keys(data)[0];
        setSelectedTemplate(defaultTemplate);
        const templateData = data[defaultTemplate];

        if (templateData) {
          const activeGroups =
            templateData.active_groups?.map((group) => ({
              label: group,
              value: group,
              type: "active",
            })) || [];

          const inactiveGroups =
            templateData.inactive_groups?.map((group) => ({
              label: group,
              value: group,
              type: "inactive",
            })) || [];

          setGroupList([
            { label: "Active Groups", items: activeGroups },
            { label: "Inactive Groups", items: inactiveGroups },
          ]);
        }
        setGroups("All");
        setIsTemplateDataLoaded(true);
      } else {
        setHasNoTemplates(true);
      }
    } catch (error) {
      console.error("Error fetching template data:", error);
      setHasNoTemplates(true);
    } finally {
      setIsLoadingModules(false);
    }
  }, []);

  useEffect(() => {
    if (!isTemplateDataLoaded || !selectedTemplate) {
      setGraphData([]);
      return;
    }
    
    const fetchGraphData = async () => {
      // Reset graph data when starting a new fetch
      setGraphData([]);
      
      const config = getApiConfig();
      const [startDate, endDate] = dateRange;
      const diffInTime = endDate.getTime() - startDate.getTime();
      const diffInDays = Math.ceil(diffInTime / (1000 * 3600 * 24));

      let reportTime;
      if (diffInDays <= 11) {
        reportTime = "daily";
      } else if (diffInDays > 11 && diffInDays <= 84) {
        reportTime = "weekly";
      } else {
        reportTime = "monthly";
      }

      const payload = {
        report_time: reportTime,
        template_name: selectedTemplate,
        group_name: groups,
      };
      try {
        const response = await axios.post(
          ApiConfig.XSurveyGraph,
          payload,
          config
        );
        // Ensure response.data is an array
        const data = Array.isArray(response.data) ? response.data : (response.data ? [response.data] : []);
        setGraphData(data);
      } catch (error) {
        console.error("Error fetching graph data:", error);
        setGraphData([]); // Set to empty array on error
      }
    };

    fetchGraphData();
  }, [dateRange, groups, selectedTemplate, isTemplateDataLoaded]);

  const updateGroupList = useCallback(
    (template) => {
      const templateData = templateList[template];
      if (templateData) {
        const activeGroups = templateData.active_groups.map((group) => ({
          label: group,
          value: group,
          type: "active",
        }));
        const inactiveGroups = templateData.inactive_groups.map((group) => ({
          label: group,
          value: group,
          type: "inactive",
        }));
        setGroupList([
          { label: "Active Groups", items: activeGroups },
          { label: "Inactive Groups", items: inactiveGroups },
        ]);
        setGroups("All");
      }
    },
    [templateList]
  );

  const handleTemplateChange = useCallback(
    (e) => {
      setSelectedTemplate(e.value);
      setGroups("All");
      updateGroupList(e.value);
    },
    [updateGroupList]
  );





  const fetchData = useCallback(async () => {
    if (!isTemplateDataLoaded) return;
    setFeedbackData([]);
    setTotalFeedback(0);
    setReceivedFeedback(0);
    setPercentage(0);
    setProgressBarPercentages({});
    setProgressBarCounts({});
    setFeedbackType("");
    setRatingType("");
    setScore(0);
    setGraphData([]);

    const [startDate, endDate] = dateRange;
    const payload = {
      start_date: startDate,
      end_date: endDate,
      group: groups,
      template: selectedTemplate,
    };
    const config = getApiConfig();
    try {
      setIsLoadingModules(true);
      const response = await axios.post(
        ApiConfig.XSurveyOverallView,
        payload,
        config
      );
      let data = response.data || [];
      let fixedData = null;
      let feedbackItems = [];


      // Handle both array and single object responses
      if (Array.isArray(data)) {
        // If data is an array, find summary and filter feedback items
        // The summary data is typically the last item in the array (without ticket_id)
        // First, try to find item with progress_bar_counts (most reliable indicator)
        fixedData = data.find((item) => 
          item && item.progress_bar_counts
        );
        
        // If not found, look for item with total_feedback but no ticket_id
        if (!fixedData) {
          fixedData = data.find((item) => 
            item && item.total_feedback !== undefined && !item.ticket_id
          );
        }
        
        // Final fallback: check if last item looks like summary data
        if (!fixedData && data.length > 0) {
          const lastItem = data[data.length - 1];
          if (lastItem && (lastItem.total_feedback !== undefined || lastItem.progress_bar_counts)) {
            fixedData = lastItem;
          }
        }
        
        // Filter out summary data to get only feedback items (items with ticket_id)
        feedbackItems = data.filter((item) => 
          item && item.ticket_id
        );
        
      } else if (data && typeof data === 'object') {
        // If data is a single object, check if it's summary or has nested structure
        if (data.progress_bar_counts || (data.total_feedback !== undefined && !data.ticket_id)) {
          // It's a summary object
          fixedData = data;
          feedbackItems = [];
        } else if (data.ticket_id) {
          // It's a single feedback item
          fixedData = null;
          feedbackItems = [data];
        } else if (data.data && Array.isArray(data.data)) {
          // Response might be wrapped in { data: [...] }
          const nestedData = data.data;
          fixedData = nestedData.find((item) => 
            item && (
              item.progress_bar_counts || 
              (item.total_feedback !== undefined && !item.ticket_id)
            )
          );
          if (!fixedData && nestedData.length > 0) {
            fixedData = nestedData[nestedData.length - 1];
          }
          feedbackItems = nestedData.filter((item) => 
            item && item.ticket_id && !item.progress_bar_counts
          );
        } else {
          // Try to use the object itself as summary if it has summary fields
          if (data.total_feedback !== undefined || data.received_feedback !== undefined || data.progress_bar_counts) {
            fixedData = data;
            feedbackItems = [];
          } else {
            console.warn("Unexpected API response structure:", data);
            fixedData = null;
            feedbackItems = [];
          }
        }
      } else {
        console.warn("API response data is not an array or object:", data);
        fixedData = null;
        feedbackItems = [];
      }

      // Check if we have valid summary data
      const hasValidSummary = fixedData && (
        fixedData.total_feedback !== undefined || 
        fixedData.received_feedback !== undefined ||
        fixedData.progress_bar_counts
      );

      if (hasValidSummary) {
        // Set data from the summary item
        const totalFeedbackValue = fixedData.total_feedback !== undefined && fixedData.total_feedback !== null ? fixedData.total_feedback : 0;
        const receivedFeedbackValue = fixedData.received_feedback !== undefined && fixedData.received_feedback !== null ? fixedData.received_feedback : 0;
        const percentageValue = fixedData.feedback_percentage !== undefined && fixedData.feedback_percentage !== null ? fixedData.feedback_percentage : 0;
        const scoreValue = fixedData?.csat_score !== undefined && fixedData?.csat_score !== null ? fixedData.csat_score : 0;
        
        // Set state values - ensure they're numbers, not strings
        setTotalFeedback(Number(totalFeedbackValue));
        setReceivedFeedback(Number(receivedFeedbackValue));
        setPercentage(Number(percentageValue));
        setProgressBarPercentages(fixedData.progress_bar_percentages || {});
        setProgressBarCounts(fixedData.progress_bar_counts || {});
        setFeedbackType(fixedData.feedback_type || "");
        setRatingType(fixedData.rating_type || "");
        setScore(Number(scoreValue));
      } else {
        // No valid summary data - set defaults
        setTotalFeedback(0);
        setReceivedFeedback(0);
        setPercentage(0);
        setProgressBarPercentages({});
        setProgressBarCounts({});
        setFeedbackType("");
        setRatingType("");
        setScore(0);
      }

      // Set feedback data
      setFeedbackData(feedbackItems);
      setIsDataLoaded(true);
    } catch (error) {
      console.error("=== ERROR in fetchData ===");
      console.error("Error object:", error);
      console.error("Error message:", error.message);
      console.error("Error response:", error.response);
      console.error("Error response data:", error.response?.data);
      console.error("Error response status:", error.response?.status);
      // Reset all data on error to prevent showing stale data - use 0 instead of null
      setFeedbackData([]);
      setTotalFeedback(0);
      setReceivedFeedback(0);
      setPercentage(0);
      setProgressBarPercentages({});
      setProgressBarCounts({});
      setFeedbackType("");
      setRatingType("");
      setScore(0);
      setGraphData([]);
    } finally {
      setIsLoadingModules(false);
    }
  }, [dateRange, groups, selectedTemplate, isTemplateDataLoaded]);

  useEffect(() => {
    fetchTemplateData();
  }, [fetchTemplateData]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);




  


  if (isLoadingModules) {
    return <Loader />;
  }

  if (hasNoTemplates) {
    return (
      <div id={id} className="xsurvey-dashboard">
        <div className="xsurvey-header-card">
          <div className="d-flex align-items-center gap-3">
            <div className="xsurvey-header-title-icon">
              <FontAwesomeIcon icon={faChartBar} />
            </div>
            <div>
              <h1 className="xsurvey-header-title mb-0">Survey Analytics</h1>
              <p className="text-muted mb-0" style={{ fontSize: "0.875rem" }}>
                Track customer satisfaction and feedback insights
              </p>
            </div>
          </div>
        </div>

        <Card className="xsurvey-metric-card border-0" style={{ maxWidth: "700px", margin: "0 auto" }}>
          <Card.Body className="xsurvey-empty-state">
            <div className="xsurvey-empty-state-icon">
              <FontAwesomeIcon icon={faChartBar} />
            </div>
            <h3 className="xsurvey-empty-state-title">Welcome to Survey Analytics!</h3>
            <p className="xsurvey-empty-state-text mb-4">
              Get started by setting up your first survey template to begin collecting valuable customer feedback.
            </p>

            <div className="text-start mb-4 w-100" style={{ maxWidth: "450px" }}>
              {[
                { num: 1, title: "Create a Survey Template", desc: "Design your feedback questions in the Module settings" },
                { num: 2, title: "Assign Groups", desc: "Choose which teams will use this survey template" },
                { num: 3, title: "Start Collecting", desc: "Surveys will be sent automatically to customers" }
              ].map((step) => (
                <div key={step.num} className="d-flex align-items-start mb-3">
                  <div
                    style={{
                      width: "32px",
                      height: "32px",
                      borderRadius: "10px",
                      background: "linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)",
                      color: "white",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontWeight: "700",
                      fontSize: "0.875rem",
                      flexShrink: 0,
                      marginRight: "1rem",
                      boxShadow: "0 2px 8px rgba(99, 102, 241, 0.3)"
                    }}
                  >
                    {step.num}
                  </div>
                  <div>
                    <strong style={{ color: "#0f172a", fontSize: "0.95rem" }}>{step.title}</strong>
                    <p className="mb-0" style={{ color: "#64748b", fontSize: "0.85rem" }}>{step.desc}</p>
                  </div>
                </div>
              ))}
            </div>

            <div className="d-flex flex-column flex-sm-row gap-3">
              <button
                className="xsurvey-btn-primary d-flex align-items-center justify-content-center gap-2"
                onClick={() => window.location.href = '/settings'}
              >
                <i className="fas fa-cog"></i>
                Go to Settings
              </button>
              <button
                className="btn btn-outline-secondary d-flex align-items-center justify-content-center gap-2"
                onClick={() => window.location.reload()}
                style={{ borderRadius: "10px", padding: "0.75rem 1.5rem" }}
              >
                <i className="fas fa-refresh"></i>
                Refresh
              </button>
            </div>
          </Card.Body>
        </Card>
      </div>
    );
  }

  if (!isDataLoaded) {
    return <Loader />;
  }

  const groupedOptions = [
    {
      label: "Options",
      items: [
        { label: "All Groups", value: "All" },
        ...groupList.flatMap((group) => group.items || []),
      ],
    },
  ];

  return (
    <div id={id} className="xsurvey-dashboard">
      <FilterProvider>
        {/* Header Section */}
        <div className="xsurvey-header-card xsurvey-animate-in">
          {/* Title Row */}
          <div className="d-flex align-items-center justify-content-between mb-3">
            <div className="d-flex align-items-center gap-3">
              <div className="xsurvey-header-title-icon">
                <FontAwesomeIcon icon={faChartBar} />
              </div>
              <div>
                <h1 className="xsurvey-header-title mb-0">Survey Analytics</h1>
              </div>
            </div>
            <Button
              onClick={() => setShowGraph(true)}
              className="xsurvey-graph-toggle d-flex align-items-center gap-2"
            >
              <FontAwesomeIcon icon={faChartBar} />
              <span>Show Graph</span>
            </Button>
          </div>

          {/* Filters Row */}
          <div className="d-flex align-items-center gap-3" style={{ flexWrap: "nowrap" }}>
            <div style={{ flex: "1", minWidth: "150px" }}>
              <Dropdown
                onChange={handleTemplateChange}
                value={selectedTemplate}
                options={Object.keys(templateList).map((key) => ({
                  label: key.replace(/_/g, ' ').split(' ').map(word =>
                    /^[a-z]+$/i.test(word) && isNaN(word) ? word.toUpperCase() : word
                  ).join(' ') + " (Template)",
                  value: key,
                }))}
                optionLabel="label"
                className="w-full"
                style={{ width: "100%", height: "40px" }}
                appendTo="self"
                placeholder="Select Template"
              />
            </div>

            <div style={{ flex: "1", minWidth: "150px" }}>
              <Dropdown
                onChange={(e) => setGroups(e.value)}
                value={groups}
                options={groupedOptions}
                optionLabel="label"
                optionGroupLabel="label"
                optionGroupChildren="items"
                className="w-full"
                style={{ width: "100%", height: "40px" }}
                appendTo="self"
                placeholder="Select Group"
              />
            </div>

            <div style={{ flex: "1.5", minWidth: "220px" }}>
              <DateRangePicker
                data-tooltip-id="date_picker"
                ranges={predefinedRanges}
                showOneCalendar
                shouldDisableDate={afterToday()}
                value={dateRange}
                onChange={setDateRange}
                style={{ width: "100%", height: "40px" }}
              />
            </div>
          </div>
        </div>
        {/* Metrics Section */}
        {isLoadingModules ? (
          <Row className="g-3 mb-4">
            <Col md={12}>
              <Loader />
            </Col>
          </Row>
        ) : (
          <Row className="g-4 mb-4" style={{ alignItems: "stretch" }}>
            <Col xs={12} md={4} lg={3}>
              <div className="xsurvey-animate-in" style={{ animationDelay: "0.1s" }}>
                <ScoreAndSurvey
                  score={score}
                  totalFeedback={totalFeedback}
                  receivedFeedback={receivedFeedback}
                  percentage={percentage}
                />
              </div>
            </Col>
            <Col xs={12} md={8} lg={9}>
              <div className="xsurvey-animate-in" style={{ animationDelay: "0.2s" }}>
                <StorageStatus
                  progressBarPercentages={progressBarPercentages}
                  progressBarCounts={progressBarCounts}
                />
              </div>
            </Col>
          </Row>
        )}

        {/* Graph Modal */}
        <Modal
          show={showGraph}
          onHide={() => setShowGraph(false)}
          size="xl"
          centered
          className="xsurvey-graph-modal"
        >
          <Modal.Header closeButton style={{
            borderBottom: "1px solid #e2e8f0",
            background: "linear-gradient(135deg, #f8fafc 0%, #ffffff 100%)",
            padding: "1rem 1.5rem"
          }}>
            <Modal.Title style={{
              fontWeight: "600",
              color: "#1e293b",
              display: "flex",
              alignItems: "center",
              gap: "0.75rem"
            }}>
              <FontAwesomeIcon icon={faChartBar} style={{ color: "#6366f1" }} />
              Survey Trends
            </Modal.Title>
          </Modal.Header>
          <Modal.Body style={{ padding: "1.5rem" }}>
            <TotalSales
              graphData={graphData}
              data={overGraph}
              dateRange={dateRange}
              selectedTemplate={selectedTemplate}
              groups={groups}
            />
          </Modal.Body>
        </Modal>

        {/* Feedback Feed Section */}
        <Row className="g-4">
          <Col xs={12}>
            <div className="xsurvey-animate-in" style={{ animationDelay: "0.4s" }}>
              <XSurveyFeed
                feedbackData={feedbackData}
                isLoading={isLoadingModules}
                onRefresh={fetchData}
                globalDateRange={dateRange}
              />
            </div>
          </Col>
        </Row>
      </FilterProvider>
    </div>
  );
};

export default XSurvey;
